import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { moment } from '@hotjar/webapp/lib/moment';

import { Button, CloseAction, tokens } from '@hotjar/hotsauce';
import { useLocalStorageJSONValue } from '@hotjar/webapp/hooks/useLocalStorageValue';
import Illustration from '@hotjar/shared-assets/images/contact-sales-banner.svg';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useCurrentSite } from '@hotjar/site';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { api as paymentMethodAPIV1 } from '@hotjar/webapp/billing/paymentMethod';
import { useOwnedAccount } from '@hotjar/account';
import type { CurrentPaymentMethod } from '@hotjar/webapp/billing/plan';
import { getSalesLink } from '@hotjar/upsell';

const MQL_CONTACT_SALES_BANNER_STORAGE_KEY = 'mqlContactSalesBannerDismissed';

const StyledBox = styled.div`
  position: relative;
  padding: ${tokens.spacer.x5};
  border: 1px solid ${tokens.color.ui_border_light};
  border-radius: ${tokens.radius.m};
  display: flex;
  flex-direction: row;
  max-width: 1050px;
  // this should be "margin: tokens.spacer.x3 auto;"
  // but there are some issues with the layout in plans & billing page
  margin: ${tokens.spacer.x3} auto 0 auto;
  ${tokens.elevation(1)}
`;

const StyledContentWrapper = styled.div`
  flex-grow: 1;
`;

const StyledImageWrapper = styled.div`
  flex-shrink: 1;
`;

const StyledCloseAction = styled(CloseAction)`
  position: absolute;
  top: ${tokens.spacer.x2};
  right: ${tokens.spacer.x2};
`;

const StyledTitle = styled.h3`
  ${tokens.text('subtitle1')};
  color: ${tokens.color.text_light};
`;

const StyledText = styled.p`
  color: ${tokens.color.text_light};
`;

const StyledButtonWrapper = styled.div`
  margin-top: ${tokens.spacer.x2};
`;

export const ContactSalesBanner = (): JSX.Element | null => {
  const { t } = useTranslation();
  const currentSite = useCurrentSite().data;
  const [subscriptionInfo, setSubscriptionInfo] = useState<Pick<
    CurrentPaymentMethod,
    'available' | 'hasPaidPlan' | 'nextBillingDate'
  > | null>(null);
  const { click } = useTrack();
  const { coveragePercentage } = useTrafficCoverage();
  const { data: account } = useOwnedAccount();

  const isOwner = !!account;
  const [bannerDismissed, setBannerDismissed] = useLocalStorageJSONValue<boolean>(
    MQL_CONTACT_SALES_BANNER_STORAGE_KEY,
  );

  const onBannerDismiss = () => {
    if (currentSite) {
      click('MQL Schedule Sales Call - Dismiss', 'settings.billing', {
        url_event_was_triggered_on: '/settings/billing',
        account_id: currentSite.account.id,
        organization_id: currentSite.organization.id,
        site_id: currentSite.id,
        shown_traffic_coverage: coveragePercentage,
      });
    }
    setBannerDismissed(true);
  };

  const openCalendly = () => {
    if (currentSite) {
      click('MQL Schedule Sales Call', 'settings.billing', {
        url_event_was_triggered_on: '/settings/billing',
        account_id: currentSite.account.id,
        organization_id: currentSite.organization.id,
        site_id: currentSite.id,
        shown_traffic_coverage: coveragePercentage,
      });
    }
    window.open(getSalesLink('BillingPage'), '_blank', 'noopener noreferrer');
  };

  useEffect(() => {
    async function fetchPaymentDetails() {
      if (account) {
        const { available, hasPaidPlan, nextBillingDate } = await paymentMethodAPIV1.get(account);
        setSubscriptionInfo({
          available,
          hasPaidPlan,
          nextBillingDate,
        });
      }
    }

    fetchPaymentDetails();
  }, [account]);

  if (!subscriptionInfo || !account) {
    return null;
  }

  const { available: hasPaymentMethods, hasPaidPlan, nextBillingDate } = subscriptionInfo;

  /*
   * This banner should not be visible if:
   * 1. it has been dismissed
   * 2. the current User is not the Owner
   * 3. the org has no Mql Sites
   * 4. the Account has paid plans
   * */

  if (
    bannerDismissed ||
    !isOwner ||
    hasPaidPlan ||
    hasPaymentMethods !== false ||
    (nextBillingDate && nextBillingDate !== null && nextBillingDate < moment.now().valueOf())
  ) {
    return null;
  }

  return (
    <StyledBox>
      <StyledContentWrapper>
        <StyledCloseAction
          description={t('billing:mql-contact-banner.dismiss-button-text')}
          onClick={onBannerDismiss}
        />
        <StyledTitle>
          <Trans i18nKey="billing:mql-contact-banner.title" />
        </StyledTitle>
        <StyledText>
          <Trans i18nKey="billing:mql-contact-banner.text" />
        </StyledText>
        <StyledButtonWrapper>
          <Button onClick={openCalendly} icon="chat">
            {t('billing:mql-contact-banner.button-text')}
          </Button>
        </StyledButtonWrapper>
      </StyledContentWrapper>
      <StyledImageWrapper>
        <img src={Illustration} alt={t('billing:mql-contact-banner.image-alt-text')} />
      </StyledImageWrapper>
    </StyledBox>
  );
};
